import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './hooks/useAuth';
import CookieBanner from './components/common/CookieBanner';

// Layout — public layout pieces stay eager (small + render on many public
// pages). DashboardLayout is lazy so landing-page visitors don't ship the
// authenticated app shell.
import PublicNavbar from './components/layout/PublicNavbar';
import Footer from './components/layout/Footer';
import SupportWidget from './components/common/SupportWidget';
const DashboardLayout = lazy(() => import('./components/layout/DashboardLayout'));

// Auth Pages (lazy — only loaded when a user actually hits /login etc.)
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const SocialCallback = lazy(() => import('./pages/auth/SocialCallback'));
const VerifyEmailSent = lazy(() => import('./pages/auth/VerifyEmailSent'));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail'));

// Dashboard Pages (lazy — keeps the landing bundle off the public bundle).
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const Calendars = lazy(() => import('./pages/dashboard/Calendars'));
const CreateCalendar = lazy(() => import('./pages/dashboard/CreateCalendar'));
const EditCalendar = lazy(() => import('./pages/dashboard/EditCalendar'));
const Bookings = lazy(() => import('./pages/dashboard/Bookings'));
const Settings = lazy(() => import('./pages/dashboard/Settings'));
const Billing = lazy(() => import('./pages/dashboard/Billing'));
const Analytics = lazy(() => import('./pages/dashboard/Analytics'));
const AIAssistant = lazy(() => import('./pages/dashboard/AIAssistant'));
const AIEmailComposer = lazy(() => import('./pages/dashboard/AIEmailComposer'));
const AIAgent = lazy(() => import('./pages/dashboard/AIAgent'));
const Availability = lazy(() => import('./pages/dashboard/Availability'));
const Contacts = lazy(() => import('./pages/dashboard/Contacts'));
const DashboardIntegrations = lazy(() => import('./pages/dashboard/Integrations'));
const Webhooks = lazy(() => import('./pages/dashboard/Webhooks'));
const AdminDashboard = lazy(() => import('./pages/dashboard/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/dashboard/AdminUsers'));
const AdminUserDetail = lazy(() => import('./pages/dashboard/AdminUserDetail'));
const AdminNewsletter = lazy(() => import('./pages/dashboard/AdminNewsletter'));
const Team = lazy(() => import('./pages/dashboard/Team'));
const TeamAuditLog = lazy(() => import('./pages/dashboard/TeamAuditLog'));

// Public Pages (lazy loaded)
const BookingWidget = lazy(() => import('./pages/public/BookingWidget'));
const LandingPage = lazy(() => import('./pages/public/LandingPage'));
const Features = lazy(() => import('./pages/public/Features'));
const Pricing = lazy(() => import('./pages/public/Pricing'));
const About = lazy(() => import('./pages/public/About'));
const Privacy = lazy(() => import('./pages/public/Privacy'));
const Terms = lazy(() => import('./pages/public/Terms'));
const Refund = lazy(() => import('./pages/public/Refund'));
const Cookies = lazy(() => import('./pages/public/Cookies'));
const Contact = lazy(() => import('./pages/public/Contact'));
const Integrations = lazy(() => import('./pages/public/Integrations'));
const ChromeExtension = lazy(() => import('./pages/public/ChromeExtension'));
const Changelog = lazy(() => import('./pages/public/Changelog'));
const Support = lazy(() => import('./pages/public/Support'));
const ApiReference = lazy(() => import('./pages/public/ApiReference'));
const Docs = lazy(() => import('./pages/public/Docs'));
const Careers = lazy(() => import('./pages/public/Careers'));
const Install = lazy(() => import('./pages/public/Install'));
const Unsubscribe = lazy(() => import('./pages/public/Unsubscribe'));
const BookingResponse = lazy(() => import('./pages/public/BookingResponse'));
const NotFound = lazy(() => import('./pages/public/NotFound'));
const AcceptInvite = lazy(() => import('./pages/auth/AcceptInvite'));

// Vertical landing pages — same Appozy product, niche-targeted marketing.
// Each gets its own lazy chunk so the main bundle isn't affected by these
// long-form pages that most visitors never load.
const ForCoaches = lazy(() => import('./pages/public/verticals/ForCoaches'));
const ForTutors = lazy(() => import('./pages/public/verticals/ForTutors'));
const ForConsultants = lazy(() => import('./pages/public/verticals/ForConsultants'));
const ForAlliedHealth = lazy(() => import('./pages/public/verticals/ForAlliedHealth'));
const ForMigrationAgents = lazy(() => import('./pages/public/verticals/ForMigrationAgents'));
const CalendlyAlternative = lazy(() => import('./pages/public/verticals/CalendlyAlternative'));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
);

// Public page wrapper with Navbar + Footer.
// Includes a skip-to-main-content link for keyboard / screen reader
// users (WCAG 2.4.1 Bypass Blocks) — only becomes visible when
// focused via Tab, otherwise visually hidden.
// Inner Suspense keeps Navbar/Footer mounted while the next lazy page chunk
// loads — without it, the outer Suspense at <Routes> would unmount the whole
// layout and the user sees a blank flash on every navigation.
const PublicPage = ({ children }) => (
  <>
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-lg focus:bg-violet-600 focus:text-white focus:font-semibold focus:shadow-lg"
    >
      Skip to main content
    </a>
    <PublicNavbar />
    <main id="main-content" tabIndex="-1">
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </main>
    <Footer />
    <SupportWidget />
  </>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" replace />;
  return children;
};

// Public Route Component (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (token) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
        <div className="min-h-screen">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Landing Page */}
              <Route path="/" element={
                localStorage.getItem('token') ? <Navigate to="/dashboard" replace /> : <LandingPage />
              } />

              

              {/* Public Booking Widget */}
              <Route path="/book/:slug" element={<BookingWidget />} />

              {/* Public Pages with Navbar + Footer */}
              <Route path="/features" element={<PublicPage><Features /></PublicPage>} />
              <Route path="/pricing" element={<PublicPage><Pricing /></PublicPage>} />
              <Route path="/about" element={<PublicPage><About /></PublicPage>} />
              <Route path="/careers" element={<PublicPage><Careers /></PublicPage>} />
              <Route path="/install" element={<PublicPage><Install /></PublicPage>} />
              {/* Unsubscribe page — bare layout, no public navbar/footer
                  since recipients land here from email and shouldn't be
                  re-marketed at while opting out. */}
              <Route path="/unsubscribe/:token" element={<Suspense fallback={<PageLoader />}><Unsubscribe /></Suspense>} />
              <Route path="/booking-response/:bookingId" element={<Suspense fallback={<PageLoader />}><BookingResponse /></Suspense>} />
              <Route path="/privacy" element={<PublicPage><Privacy /></PublicPage>} />
              <Route path="/terms" element={<PublicPage><Terms /></PublicPage>} />
              <Route path="/refund" element={<PublicPage><Refund /></PublicPage>} />
              <Route path="/cookies" element={<PublicPage><Cookies /></PublicPage>} />
              <Route path="/contact" element={<PublicPage><Contact /></PublicPage>} />
              <Route path="/integrations" element={<PublicPage><Integrations /></PublicPage>} />
              <Route path="/chrome-extension" element={<PublicPage><ChromeExtension /></PublicPage>} />
              <Route path="/changelog" element={<PublicPage><Changelog /></PublicPage>} />
              <Route path="/support" element={<PublicPage><Support /></PublicPage>} />
              <Route path="/api" element={<PublicPage><ApiReference /></PublicPage>} />

              {/* Vertical landing pages — same product, niche-targeted SEO + copy. */}
              <Route path="/for/coaches" element={<PublicPage><ForCoaches /></PublicPage>} />
              <Route path="/for/tutors" element={<PublicPage><ForTutors /></PublicPage>} />
              <Route path="/for/consultants" element={<PublicPage><ForConsultants /></PublicPage>} />
              <Route path="/for/allied-health" element={<PublicPage><ForAlliedHealth /></PublicPage>} />
              <Route path="/for/migration-agents" element={<PublicPage><ForMigrationAgents /></PublicPage>} />

              {/* Comparison / SEO landing — targets "Calendly alternative (Australia)". */}
              <Route path="/calendly-alternative" element={<PublicPage><CalendlyAlternative /></PublicPage>} />

              {/* Auth Routes */}
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />

              <Route path="/auth/social-callback" element={<SocialCallback />} />
              <Route path="/team/accept-invite" element={<AcceptInvite />} />
              <Route path="/verify-email-sent" element={<VerifyEmailSent />} />
              <Route path="/verify-email/:token" element={<VerifyEmail />} />

              {/* Dashboard Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="calendars" element={<Calendars />} />
                <Route path="calendars/create" element={<CreateCalendar />} />
                <Route path="calendars/:id/edit" element={<EditCalendar />} />
                <Route path="bookings" element={<Bookings />} />
                <Route path="settings" element={<Settings />} />
                <Route path="billing" element={<Billing />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="ai-assistant" element={<AIAssistant />} />
                <Route path="ai-email-composer" element={<AIEmailComposer />} />
                <Route path="ai-agent" element={<AIAgent />} />
                <Route path="availability" element={<Availability />} />
                <Route path="contacts" element={<Contacts />} />
                <Route path="integrations" element={<DashboardIntegrations />} />
                <Route path="webhooks" element={<Webhooks />} />
                <Route path="admin" element={<AdminDashboard />} />
                <Route path="admin/users" element={<AdminUsers />} />
                <Route path="admin/users/:id" element={<AdminUserDetail />} />
                <Route path="admin/newsletter" element={<AdminNewsletter />} />
                <Route path="team" element={<Team />} />
                <Route path="team/audit" element={<TeamAuditLog />} />
              </Route>

              <Route path="/docs" element={<PublicPage><Docs /></PublicPage>} />

              {/* 404 — must stay last so explicit routes match first */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>

          <CookieBanner />

          <ToastContainer
            position="bottom-left"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            // Bottom-left placement keeps toasts out of the way of:
            //   - the navbar / profile menu / bell up top
            //   - the AI chat + Support widgets stacked at bottom-right
            //   - the Notes widget at top-right
            // Explicit left/bottom offsets so the toast clears the
            // dashboard sidebar (256px wide on desktop, hidden on
            // mobile). Width clamp keeps toasts off the right edge
            // on narrow screens.
            style={{ left: '16px', bottom: '16px', width: 'min(360px, calc(100vw - 32px))' }}
          />
        </div>
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;